import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, LayoutGrid, List, Plus, Users } from "lucide-react";
import { useLets } from "@/hooks/useLets";
import { useAgent } from "@/hooks/useAgent";
import { can } from "@/lib/roles";
import { cn } from "@/lib/utils";
import { AgentLayout } from "@/components/agent/AgentLayout";
import { AddPropertyDialog } from "@/components/agent/AddPropertyDialog";
import { PropertyImage } from "@/components/agent/PropertyImage";
import { EmptyState, ListingTag, Loading, PageHeader, PrimaryButton, SecondaryButton } from "@/components/agent/ui";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { gbp, shortDate } from "@/lib/format";

const VIEW_KEY = "landin-property-view";
const SORTS = {
  newest: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
  rent: (a, b) => a.monthly_rent - b.monthly_rent,
  status: (a, b) => a.listing_status.localeCompare(b.listing_status),
};
const count = (l) => `${l.applicants.length} applicant${l.applicants.length === 1 ? "" : "s"}`;

const Card = ({ l, i }) => (
  <Link to={`/lets/${l.id}/edit`} className="group flex flex-col overflow-hidden rounded-xl border bg-background p-1 transition-shadow hover:shadow-md" data-testid={`property-card-${i}`}>
    <div className="relative aspect-[4/3] overflow-hidden rounded-lg bg-muted">
      <div className="h-full w-full transition-transform duration-300 group-hover:scale-[1.02]"><PropertyImage src={l.image_data} testid={`property-image-${i}`} /></div>
      <span className="absolute left-2 top-2 rounded-full bg-background shadow-xs"><ListingTag status={l.listing_status} testid={`property-listing-${i}`} /></span>
    </div>
    <div className="px-2.5 pb-2 pt-2.5">
      <p className="truncate text-sm font-medium" data-testid={`property-address-${i}`}>{l.address}</p>
      <div className="mt-0.5 flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span data-testid={`property-rent-${i}`}><span className="font-medium text-foreground">{gbp(l.monthly_rent)}</span> a month</span>
        <span className="inline-flex items-center gap-1" data-testid={`property-applicants-${i}`}><Users className="size-3" />{l.applicants.length}</span>
      </div>
    </div>
  </Link>
);

const Row = ({ l, i }) => (
  <Link to={`/lets/${l.id}/edit`} className="flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-accent" data-testid={`property-row-${i}`}>
    <span className="h-10 w-14 shrink-0 overflow-hidden rounded-md"><PropertyImage src={l.image_data} /></span>
    <span className="min-w-0 flex-1">
      <span className="block truncate text-sm font-medium" data-testid={`property-address-${i}`}>{l.address}</span>
      <span className="block text-xs text-muted-foreground">Move in {shortDate(l.move_in_date)} · {count(l)}</span>
    </span>
    <span className="hidden w-28 text-right tabular-nums sm:block">{gbp(l.monthly_rent)}</span>
    <span className="w-24 text-right"><ListingTag status={l.listing_status} testid={`property-listing-${i}`} /></span>
  </Link>
);

const ViewToggle = ({ view, onChange }) => (
  <div className="flex items-center gap-0.5 rounded-full border p-0.5" data-testid="property-view-toggle">
    {[["cards", LayoutGrid], ["list", List]].map(([v, Icon]) => (
      <Button key={v} variant="ghost" size="icon-xs" aria-label={v === "cards" ? "Cards" : "List"} aria-pressed={view === v}
        className={cn("rounded-full", view === v && "bg-accent")} onClick={() => onChange(v)} data-testid={`property-view-${v}`}>
        <Icon />
      </Button>
    ))}
  </div>
);

export default function Properties() {
  const navigate = useNavigate();
  const location = useLocation();
  const agent = useAgent();
  const { lets, error, load } = useLets();
  const [sort, setSort] = useState("newest");
  const [view, setView] = useState(() => sessionStorage.getItem(VIEW_KEY) || localStorage.getItem("landin-default-property-view") || "cards");
  const adding = location.pathname === "/lets/new";
  const editable = can(agent?.role, "edit");
  const sorted = useMemo(() => [...(lets || [])].sort(SORTS[sort]), [lets, sort]);
  const available = (lets || []).filter((l) => l.listing_status !== "rented").length;

  const pickView = (v) => {
    sessionStorage.setItem(VIEW_KEY, v);
    setView(v);
  };

  let body;
  if (error) body = <EmptyState title="Couldn't load properties" text={error} action={<SecondaryButton onClick={load} testid="properties-retry">Try again</SecondaryButton>} testid="properties-error" />;
  else if (!lets) body = <Loading testid="properties-loading" />;
  else if (!lets.length) body = <EmptyState icon={Home} title="No properties yet" text="Create a property to manage it. You'll get an apply link to send to people, and see their progress here." action={editable && <SecondaryButton to="/lets/new" testid="empty-add-property"><Plus /> Add property</SecondaryButton>} testid="properties-empty" />;
  else if (view === "cards")
    body = <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4" data-testid="properties-cards">{sorted.map((l, i) => <Card key={l.id} l={l} i={i} />)}</div>;
  else body = <div className="flex flex-col" data-testid="properties-list">{sorted.map((l, i) => <Row key={l.id} l={l} i={i} />)}</div>;

  const actions = (
    <div className="flex items-center gap-2">
      <Select value={sort} onValueChange={setSort}>
        <SelectTrigger size="sm" className="rounded-full" data-testid="property-sort-select"><SelectValue /></SelectTrigger>
        <SelectContent align="end">
          <SelectItem value="newest" data-testid="property-sort-newest">Newest</SelectItem>
          <SelectItem value="rent" data-testid="property-sort-rent">Rent</SelectItem>
          <SelectItem value="status" data-testid="property-sort-status">Status</SelectItem>
        </SelectContent>
      </Select>
      <ViewToggle view={view} onChange={pickView} />
      {editable && <PrimaryButton to="/lets/new" testid="add-property-button"><Plus /> Add property</PrimaryButton>}
    </div>
  );

  return (
    <AgentLayout wide>
      <PageHeader title="Properties" description={lets ? `${lets.length} propert${lets.length === 1 ? "y" : "ies"} · ${available} available` : " "} action={actions} />
      {body}
      <AddPropertyDialog open={adding && editable} onClose={() => navigate("/properties")} />
    </AgentLayout>
  );
}
